import React from 'react';
import { XIcon, DollarSignIcon, CalendarIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, FileTextIcon, DownloadIcon, PrinterIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type PaymentStatus = 'Paid' | 'Scheduled' | 'Delayed' | 'Failed';
interface Payment {
  id: number;
  amount: string;
  date: string;
  status: PaymentStatus;
  description: string;
  reference: string;
  paymentMethod: string;
  account: string;
  projectTitle: string;
}
interface PaymentDetailsModalProps {
  payment: Payment;
  onClose: () => void;
}
export const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  payment,
  onClose
}) => {
  // Get status color
  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'Delayed':
        return 'bg-yellow-100 text-yellow-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = (status: PaymentStatus) => {
    switch (status) {
      case 'Paid':
        return <CheckCircleIcon size={18} className="text-green-500" data-id="element-488" />;
      case 'Scheduled':
        return <ClockIcon size={18} className="text-blue-500" data-id="element-489" />;
      case 'Delayed':
        return <AlertCircleIcon size={18} className="text-yellow-500" data-id="element-490" />;
      case 'Failed':
        return <XCircleIcon size={18} className="text-red-500" data-id="element-491" />;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-492">
      <motion.div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} data-id="element-493">
        <div className="flex justify-between items-center p-4 border-b" data-id="element-494">
          <h2 className="text-xl font-semibold" data-id="element-495">Payment Details</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" data-id="element-496">
            <XIcon size={20} data-id="element-497" />
          </button>
        </div>
        <div className="p-6" data-id="element-498">
          <div className="flex items-center justify-between mb-6" data-id="element-499">
            <div data-id="element-500">
              <p className="text-gray-500 text-sm" data-id="element-501">Project</p>
              <h3 className="text-xl font-semibold" data-id="element-502">{payment.projectTitle}</h3>
            </div>
            <div className={`px-3 py-1 rounded-full flex items-center ${getStatusColor(payment.status)}`} data-id="element-503">
              {getStatusIcon(payment.status)}
              <span className="ml-1 text-sm font-medium" data-id="element-504">{payment.status}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-505">
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-506">
              <div className="flex items-center mb-2" data-id="element-507">
                <DollarSignIcon size={18} className="text-leaf-green mr-2" data-id="element-508" />
                <h4 className="font-medium" data-id="element-509">Amount</h4>
              </div>
              <p className="text-2xl font-bold" data-id="element-510">{payment.amount}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-511">
              <div className="flex items-center mb-2" data-id="element-512">
                <CalendarIcon size={18} className="text-leaf-green mr-2" data-id="element-513" />
                <h4 className="font-medium" data-id="element-514">Payment Date</h4>
              </div>
              <p className="text-2xl font-bold" data-id="element-515">
                {new Date(payment.date).toLocaleDateString('en-MY', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
          <div className="space-y-4 mb-6" data-id="element-516">
            <div data-id="element-517">
              <p className="text-gray-500 text-sm" data-id="element-518">Description</p>
              <p className="font-medium" data-id="element-519">{payment.description}</p>
            </div>
            <div data-id="element-520">
              <p className="text-gray-500 text-sm" data-id="element-521">Reference Number</p>
              <p className="font-medium" data-id="element-522">{payment.reference}</p>
            </div>
            <div data-id="element-523">
              <p className="text-gray-500 text-sm" data-id="element-524">Payment Method</p>
              <div className="flex items-center" data-id="element-525">
                <div size={16} className="text-gray-500 mr-2" data-id="element-526" />
                <p className="font-medium" data-id="element-527">{payment.paymentMethod}</p>
              </div>
            </div>
            <div data-id="element-528">
              <p className="text-gray-500 text-sm" data-id="element-529">Account</p>
              <p className="font-medium" data-id="element-530">{payment.account}</p>
            </div>
          </div>
          {payment.status === 'Paid' && <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6" data-id="element-531">
              <div className="flex items-start" data-id="element-532">
                <CheckCircleIcon size={20} className="text-green-500 mr-2 mt-0.5" data-id="element-533" />
                <div data-id="element-534">
                  <h4 className="font-medium text-green-800" data-id="element-535">
                    Payment Completed
                  </h4>
                  <p className="text-green-700 text-sm" data-id="element-536">
                    This payment has been successfully processed and recorded in
                    the system.
                  </p>
                </div>
              </div>
            </div>}
          {payment.status === 'Scheduled' && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-537">
              <div className="flex items-start" data-id="element-538">
                <ClockIcon size={20} className="text-blue-500 mr-2 mt-0.5" data-id="element-539" />
                <div data-id="element-540">
                  <h4 className="font-medium text-blue-800" data-id="element-541">
                    Payment Scheduled
                  </h4>
                  <p className="text-blue-700 text-sm" data-id="element-542">
                    This payment is scheduled for future disbursement. You'll
                    receive a notification when it's processed.
                  </p>
                </div>
              </div>
            </div>}
          <div className="flex justify-between pt-4 border-t" data-id="element-543">
            <div className="flex space-x-2" data-id="element-544">
              <button className="btn btn-outline flex items-center text-sm" data-id="element-545">
                <PrinterIcon size={16} className="mr-2" data-id="element-546" />
                Print
              </button>
              <button className="btn btn-outline flex items-center text-sm" data-id="element-547">
                <DownloadIcon size={16} className="mr-2" data-id="element-548" />
                Download
              </button>
            </div>
            {payment.status === 'Scheduled' && <button className="btn btn-primary text-sm" data-id="element-549">
                Process Payment
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
};